import Axios from "axios";
import { message } from "antd";
import qs from "qs";
import { getStorage } from "@/lib/webStorage";
import { changeSpin } from "@/stores/loading/action";
import { dataTrim, kickUnauthorized } from "@/lib/apiFunction";
import { getLangList } from "@/lib/i18n";
import apis from "./apis";

const CancelToken = Axios.CancelToken;
const source = CancelToken.source();
const pending: any = [];
const unauthorized: any = [];

// 可以連打的api list
const continuousApi: any = [
  apis.announcement.calendarInfo,
  apis.user.uploadUserFile,
  apis.user.userFileInfo,
];
// 可連打與略過轉圈的api list
const noPendingSpinList: any = [
  apis.bus.studentInfo,
  apis.announcement.announcementInfo,
  apis.announcement.classroomCountingPeople,
  apis.bus.schoolInfo,
];

const instance = Axios.create({
  baseURL: "http://abraham-shric.com/api/", // PRODUCTION 環境使用
  // baseURL: "http://localhost:3000/cram/api/", // DEV 環境使用
  method: "get",
  headers: {
    Authorization: "",
  },
  params: {},
  data: {},
  responseType: "json",
  timeout: 5 * 60 * 1000,
  withCredentials: true,
});

instance.interceptors.request.use(
  config => {
    const store = window.store;
    const originalUrl = config.url?.split('?')[0] ?? '';
    const isNoPendingSpin = noPendingSpinList.some((item: any) => item === `${originalUrl}`); // true: api可連打與略過轉圈
    if (!isNoPendingSpin) {
      store.dispatch(changeSpin(true));
      if (pending.indexOf(originalUrl) >= 0 && continuousApi.indexOf(originalUrl) === -1) {
        config.cancelToken = source.token;
        source.cancel("请求过于频繁！");
      } else {
        pending.push(config.url);
      }
    }
    const token = getStorage("token");

    config.headers.Authorization = token || "";

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

const resetPending = (rs: string) => {
  const index = pending.indexOf(rs);
  if (index > -1) {
    pending.splice(index, 1);
  }
  const store = window.store;
  if (pending.length < 1) {
    store.dispatch(changeSpin(false));
  }
};

instance.interceptors.response.use(
  response => {
    const { data, config = {} } = response;
    resetPending(`${config.url}`);

    return data;
  },
  error => {
    if (error.response) {
      const t: any = getLangList("errorCode");
      const { data, status, config = {} } = error.response;
      resetPending(`${config.url}`);
      if (status === 401) {
        unauthorized.push(config.url);
        if (unauthorized.length === 1) {
          kickUnauthorized();
        }
      } else if (status === 500) {
        message.warning(t[data?.errorCode] ?? data?.message ?? data?.errorCode ?? t.tac_maintenance);
      } else if (status === 503) {
        message.warning("系统维护中...");
      } else if (status === 400) {
        message.warning(t[data?.errorCode] ?? data?.message);
      } else if (status === 504) {
        message.warning(t.FAILED);
      }
      return Promise.reject(error);
    } else {
      message.warning(error?.message);
      return Promise.resolve(error);
    }
  }
);

function paramsEmpty(data: any, type: boolean) {
  if (type) {
    for (const key in data) {
      if (data[key] === null || data[key] === undefined) {
        delete data[key];
      } else if (
        typeof data[key] === "object" &&
        Object.keys(data[key]).length < 1
      ) {
        delete data[key];
      }
    }
    return data;
  } else {
    return data;
  }
}

const requestMethod = {
  get: (url: string, data: any = {}, headers: Object = {}) => {
    return instance.get(url, { params: dataTrim(data), headers });
  },

  getBlob: (url: string, data: any = {}, headers: Object = {}) => {
    return instance.get(url, { params: dataTrim(data), headers, responseType: "blob" });
  },

  // 加入多個額外data帶入網址
  duplicateParamsGet: (url: string, data = {}, headers: Object = {}, type: boolean = false) => {
    const { urlParams, dataParams }: any = data;
    const { paramsName = '', paramsArray = [], paramsDataName = '', paramsData = '' } = urlParams;
    let addUrl = `${paramsDataName}=${paramsData}`;
    paramsArray.forEach((item: any) => {
      addUrl += `&${paramsName}=${item}`;
    });
    return instance.get(`${url}?${qs.stringify(paramsEmpty(dataTrim(dataParams), type))}&${addUrl}`, { headers });
  },

  idGet: (url: string, id: string, headers: Object = {}) => {
    return instance.get(`${url}/${id}`, { headers });
  },

  idGetBlob: (url: string, id: string, headers: Object = {}) => {
    return instance.get(`${url}/${id}`, { headers, responseType: "blob" });
  },

  post: (url: string, data: any = {}, headers: Object = {}, type: boolean = false) => {
    return instance.post(url, paramsEmpty(dataTrim(data), type), { headers });
  },

  formPost: (url: string, data: any = {}, headers: Object = {}, type: boolean = false) => {
    return instance.post(url, qs.stringify(paramsEmpty(dataTrim(data), type)), { headers });
  },

  // 全部參數帶入網址
  stringPost: (url: string, data: any = {}, headers: Object = {}, type: boolean = false) => {
    return instance.post(`${url}?${qs.stringify(paramsEmpty(dataTrim(data), type))}`, "", { headers });
  },

  // 部分參數帶入網址 部分參數post出去
  stringDataPost: (url: string, { urlParams = {}, dataParams = {} } = {}, headers: Object = {}, type: boolean = false) => {
    return instance.post(`${url}?${qs.stringify(paramsEmpty(urlParams, type))}`, paramsEmpty(dataTrim(dataParams), type), { headers });
  },

  uploadPost: (url: string, data: any = {}, headers: Object = {}, type: boolean = false) => {
    const list: any = paramsEmpty(dataTrim(data), type);
    const params = new FormData();
    for (const key in list) {
      const listData = list[key];
      if (key === "file" && Array.isArray(listData)) { // 名為file 且為陣列
        listData.forEach((item: any) => {
          params.append(key, item);
        });
      } else {
        params.append(key, list[key]);
      }
    }
    return instance.post(url, params, { headers });
  },

  formJsonPost: (url: string, data: any = {}, headers: Object = {}) => {
    const { stringFormat = {}, fileFormat = {}, isStringTypeToJSON = true } = data;
    const formData = new FormData();
    Object.entries(stringFormat).forEach(([key, value]: any) => {
      formData.append(key, isStringTypeToJSON ? JSON.stringify(value) : value);
    });
    Object.entries(fileFormat).forEach(([key, value]: any) => {
      formData.append(key, value ? (value[0]?.originFileObj ?? value) : '');
    });
    return instance.post(url, formData, { headers });
  },

  formJsonStringPost: (url: string, data: any = {}, headers: Object = {}, type: boolean = false) => {
    const { urlParams = {}, fileFormat = {} } = data;
    const formData = new FormData();
    Object.entries(fileFormat).forEach(([key, value]: any) => {
      value && formData.append(key, value);
    });
    return instance.post(`${url}?${qs.stringify(paramsEmpty(urlParams, type))}`, formData, { headers });
  },

  put: (url: string, data: any = {}, headers: Object = {}, type: boolean = false) => {
    return instance.put(url, paramsEmpty(dataTrim(data), type), { headers });
  },

  // 部分參數帶入網址 部分參數put出去
  stringDataPut: (url: string, { urlParams = {}, dataParams = {} } = {}, headers: Object = {}, type: boolean = false) => {
    return instance.put(`${url}?${qs.stringify(paramsEmpty(urlParams, type))}`, paramsEmpty(dataTrim(dataParams), type), { headers });
  },

  stringPut: (url: string, data: any = {}, headers: Object = {}, type: boolean = false) => {
    return instance.put(`${url}?${qs.stringify(paramsEmpty(dataTrim(data), type))}`, "", { headers });
  },

  formJsonPut: (url: string, data: any = {}, headers: Object = {}) => {
    const { stringFormat = {}, fileFormat = {} } = data;
    const formData = new FormData();
    Object.entries(stringFormat).forEach(([key, value]: any) => {
      formData.append(key, JSON.stringify(value));
    });
    Object.entries(fileFormat).forEach(([key, value]: any) => {
      value && formData.append(key, value[0].originFileObj);
    });
    return instance.put(url, formData, { headers });
  },

  delete: (url: string, data: any = {}, headers: Object = {}) => {
    return instance.delete(`${url}?${qs.stringify(dataTrim(data))}`, { headers });
  },

  dataDelete: (url: string, data: any = {}, headers: Object = {}, type: boolean = false) => {
    return instance.delete(url, { data: paramsEmpty(dataTrim(data), type), headers });
  },

  idDelete: (url: string, id: string, headers: Object = {}) => {
    return instance.delete(`${url}/${id}`, { headers });
  },
};

export default requestMethod;